import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import { getCookie } from "../config/webStorage";
import { apiurl } from "../config/config";
import * as XLSX from "xlsx";

/* -------- FINISHED GOODS CATEGORIES -------- */
const FINISHED_GOODS_CATEGORIES = [
  { value: "tablet", label: "Tablet" },
  { value: "capsule", label: "Capsule" },
  { value: "powder", label: "Powder" },
  { value: "liquid", label: "Liquid" },
  { value: "syrup", label: "Syrup" },
  { value: "oil", label: "Oil" },
];

const FinishedGoods = () => {
  const token = getCookie("SWFMSToken");

  /* -------- STATES -------- */
  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [stockStatus, setStockStatus] = useState("");

  /* -------- FETCH FINISHED GOODS -------- */
  useEffect(() => {
    const fetchFinishedGoods = async () => {
      try {
        const res = await fetch(`${apiurl}/api/finished-goods`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        });
        const result = await res.json();
        if (result.success) setGoods(result.data || []);
      } catch (error) {
        console.error("Finished goods fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFinishedGoods();
  }, [token]);

  /* -------- FILTER LOGIC -------- */
  const filteredGoods = goods.filter((item) => {
    const matchSearch = item.productName
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchCategory = category ? item.category === category : true;

    const matchStock =
      stockStatus === ""
        ? true
        : stockStatus === "in_stock"
        ? item.quantity > item.minStockLevel
        : stockStatus === "low_stock"
        ? item.quantity <= item.minStockLevel && item.quantity > 0
        : item.quantity === 0;

    return matchSearch && matchCategory && matchStock;
  });

  /* -------- EXCEL DOWNLOAD -------- */
  const downloadExcel = () => {
    if (!filteredGoods.length) return;

    const excelData = filteredGoods.map((item) => ({
      "Product Code": item.productCode,
      "Product Name": item.productName,
      Category: item.category,
      Quantity: item.quantity,
      "Unit Price": item.unitPrice,
      "Min Stock": item.minStockLevel,
      Status:
        item.quantity === 0
          ? "Out of Stock"
          : item.quantity <= item.minStockLevel
          ? "Low Stock"
          : "In Stock",
      Batch: item.batchNo || "",
      Expiry: item.expiryDate || "",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Finished Goods");

    XLSX.writeFile(
      wb,
      `finished_goods_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  /* -------- RESET -------- */
  const resetFilters = () => {
    setSearch("");
    setCategory("");
    setStockStatus("");
  };

  return (
    <Layout>
      <Container overflow="overflow-auto">
        <div className="p-6">

          {/* HEADER */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Finished Goods</h1>

            <input
              type="text"
              placeholder="Search product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mt-3 md:mt-0 px-4 py-2 border rounded w-full md:w-64"
            />
          </div>

          {/* FILTERS */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All Categories</option>
              {FINISHED_GOODS_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>

            <select
              value={stockStatus}
              onChange={(e) => setStockStatus(e.target.value)}
              className="p-2 border rounded"
            >
              <option value="">All Stock</option>
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>

            <button
              onClick={downloadExcel}
              className="bg-[#4B49AC] text-white rounded px-4 py-2"
            >
              Download Excel
            </button>

            <button
              onClick={resetFilters}
              className="bg-gray-200 rounded px-4 py-2"
            >
              Reset
            </button>
          </div>

          {/* TABLE */}
          {loading ? (
            <p className="text-center text-gray-500">
              Loading finished goods...
            </p>
          ) : (
            <div className="overflow-x-auto bg-white shadow rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="border p-2">Product Code</th>
                    <th className="border p-2">Product Name</th>
                    <th className="border p-2">Category</th>
                    <th className="border p-2">Quantity</th>
                    <th className="border p-2">Unit Price</th>
                    <th className="border p-2">Min Stock</th>
                    <th className="border p-2">Status</th>
                    <th className="border p-2">Batch</th>
                    <th className="border p-2">Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGoods.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="border p-2">{item.productCode}</td>
                      <td className="border p-2">{item.productName}</td>
                      <td className="border p-2">{item.category}</td>
                      <td className="border p-2">{item.quantity}</td>
                      <td className="border p-2">₹{item.unitPrice}</td>
                      <td className="border p-2">{item.minStockLevel}</td>
                      <td className="border p-2">
                        {item.quantity === 0 ? (
                          <span className="text-red-600">Out</span>
                        ) : item.quantity <= item.minStockLevel ? (
                          <span className="text-yellow-600">Low</span>
                        ) : (
                          <span className="text-green-600">In</span>
                        )}
                      </td>
                      <td className="border p-2">{item.batchNo || "-"}</td>
                      <td className="border p-2">{item.expiryDate || "-"}</td>
                    </tr>
                  ))}

                  {filteredGoods.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center p-6 text-gray-500">
                        No finished goods found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default FinishedGoods;